import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCharacterCatalogStore } from '../stores/characterCatalogStore';
import { useCharacterCreationStore } from '../stores/characterCreationStore';
import { useRulesetIdFromUrl } from '../hooks/useUrlRuleset';
import { CharacterCard } from '../components/library/CharacterCard';
import { LibraryEmptyState } from '../components/library/LibraryEmptyState';
import { LibraryToolbar } from '../components/library/LibraryToolbar';
import { LocalStorageWarning } from '../components/library/LocalStorageWarning';
import { ImportLibraryDialog } from '../components/library/ImportLibraryDialog';
import { downloadCatalogJson } from '../utils/catalogExport';
import { saveCharacter } from '../utils/characterDatabase';
import { hasCharacterData } from '../utils/draftUtils';
import { getRulesetConfig } from '@character-creator/data';

/**
 * Character Library page - displays all saved characters for the current ruleset.
 * Users can view, edit, duplicate, rename, and delete their characters.
 */
export function CharacterLibraryPage() {
  const rulesetId = useRulesetIdFromUrl();
  const config = getRulesetConfig(rulesetId);
  const {
    isLoading,
    error,
    initializeCatalog,
    getCharactersByRuleset,
    deleteCharacterFromCatalog,
    duplicateCharacterInCatalog,
    renameCharacterInCatalog,
  } = useCharacterCatalogStore();

  const navigate = useNavigate();
  const { draft, reset, setRulesetId } = useCharacterCreationStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [showImportDialog, setShowImportDialog] = useState(false);

  // Initialize catalog on mount
  useEffect(() => {
    initializeCatalog();
  }, [initializeCatalog]);

  // Get characters for current ruleset
  const characters = getCharactersByRuleset(rulesetId);
  const allCharacters = useCharacterCatalogStore.getState().characters;

  // Export all characters as JSON
  const handleExportAll = () => {
    downloadCatalogJson(allCharacters);
  };

  // Open import dialog
  const handleOpenImport = () => {
    setShowImportDialog(true);
  };

  // Handle import completion - refresh the catalog
  const handleImportComplete = () => {
    initializeCatalog();
  };

  // Filter by search query
  const filteredCharacters = searchQuery
    ? characters.filter((char) =>
        char.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : characters;

  // Loading state
  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-amber-500 border-t-transparent" />
          <span className="text-slate-400">Loading characters...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <div className="rounded-lg bg-red-900/20 p-6 text-center">
          <p className="text-red-400">{error}</p>
          <button
            onClick={() => initializeCatalog()}
            className="mt-4 rounded bg-red-600 px-4 py-2 text-white hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Local Storage Warning Banner */}
      <LocalStorageWarning onExportClick={handleExportAll} />

      {/* Page Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-white">
            My Characters
          </h1>
          <p className="mt-1 text-slate-400">
            {config?.name ?? rulesetId} • {characters.length}{' '}
            {characters.length === 1 ? 'character' : 'characters'}
          </p>
        </div>
        <button
          type="button"
          onClick={async () => {
            if (hasCharacterData(draft)) {
              await saveCharacter(draft);
            }
            reset();
            setRulesetId(rulesetId);
            navigate(`/${rulesetId}/create`, { state: { freshStart: true } });
          }}
          className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 font-medium text-white transition-colors hover:bg-amber-700"
        >
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          New Character
        </button>
      </div>

      {/* Toolbar (only show if characters exist) */}
      {characters.length > 0 && (
        <LibraryToolbar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          characterCount={filteredCharacters.length}
          onExportAll={handleExportAll}
          onImport={handleOpenImport}
        />
      )}

      {/* Content */}
      {characters.length === 0 ? (
        <LibraryEmptyState rulesetId={rulesetId} />
      ) : filteredCharacters.length === 0 ? (
        <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-8 text-center">
          <p className="text-slate-400">
            No characters match "{searchQuery}"
          </p>
          <button
            onClick={() => setSearchQuery('')}
            className="mt-2 text-amber-500 hover:text-amber-400"
          >
            Clear search
          </button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredCharacters.map((character) => (
            <CharacterCard
              key={character.id}
              character={character}
              rulesetId={rulesetId}
              onDelete={() => deleteCharacterFromCatalog(character.id)}
              onDuplicate={() => duplicateCharacterInCatalog(character.id)}
              onRename={(newName) =>
                renameCharacterInCatalog(character.id, newName)
              }
            />
          ))}
        </div>
      )}

      {/* Import Dialog */}
      <ImportLibraryDialog
        isOpen={showImportDialog}
        onClose={() => setShowImportDialog(false)}
        onImportComplete={handleImportComplete}
        existingCharacters={allCharacters}
      />
    </div>
  );
}
